import { motion } from "framer-motion";
import {
  TrafficCone, ShieldQuestion, Plane, CloudRainWind, Compass, Users, Target,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CtaBanner from "@/components/CtaBanner";
import { MaskedLine } from "@/components/MaskedLine";

const ANXIETIES = [
  { icon: TrafficCone, text: "Did I account for traffic?" },
  { icon: ShieldQuestion, text: "Will TSA lines be longer than usual?" },
  { icon: Plane, text: "What if the flight is delayed or the gate changes?" },
  { icon: CloudRainWind, text: "Did I pack right for the weather and my meetings?" },
];

const PILLARS = [
  "Real-time traffic and transit data",
  "Live flight status with delay predictions",
  "Weather- and activity-aware packing lists",
  "Smart departure timing",
  "Airport-specific insight: TSA waits and parking",
  "Community-driven data modeling",
];

const CARDS = [
  {
    icon: Compass, title: "Our Story",
    body: "Tripsee was created by frequent business travelers to take the stress out of getting to your flight, combining smart planning, real-time data, and peace of mind in one simple assistant.",
  },
  {
    icon: Users, title: "Our Team",
    body: "A crew of experienced travelers, builders, and problem-solvers on a mission to make air travel smoother, smarter, and less stressful for everyone.",
  },
  {
    icon: Target, title: "Our Goal",
    body: "Simplify the journey from doorstep to gate and turn what used to be tribal travel knowledge into a friendly, accessible app for everyone.",
  },
];

export default function About() {
  return (
    <main>
      <Seo
        title="About — Tripsee"
        description="Tripsee was born from 15 years of business-travel anxiety. Meet the team restoring calm and control to the door-to-gate journey."
      />

      <section className="relative pt-40 pb-24 overflow-hidden bg-radar-grid">
        <div className="absolute inset-0 ambient-glow" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-6">About Tripsee</p>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-white max-w-4xl">
            <MaskedLine delay={0.1}>Born in the</MaskedLine>
            <MaskedLine delay={0.26} className="text-primary text-glow">security line.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1527631746610-bca00a040d60?q=80&w=1000&auto=format&fit=crop"
                alt="Traveler checking their phone at the airport"
                loading="lazy"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div className="mt-6 overflow-hidden rounded-2xl border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=800&auto=format&fit=crop"
                alt="Business traveler preparing for a trip"
                loading="lazy"
                className="w-full h-64 object-cover"
              />
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <h2 className="font-display text-2xl sm:text-4xl font-bold tracking-tight text-white leading-tight">
              Fifteen years of sales travel taught us one thing: the hardest part of flying is
              everything before the plane.
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed">
              Our founder spent over a decade living out of airports, hotel lobbies, rideshares, and
              check-in lines. Life on the road got easier, except one thing never did: the stress of
              getting from the front door to the gate.
            </p>
            <p className="mt-4 text-slate-400 leading-relaxed">
              Every trip carried the same anxiety:
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ANXIETIES.map((a) => (
                <div key={a.text} className="glass-panel rounded-xl p-5 flex items-start gap-3">
                  <a.icon size={18} className="text-[#FFB100] mt-0.5 shrink-0" />
                  <p className="text-sm text-slate-300 leading-relaxed">{a.text}</p>
                </div>
              ))}
            </div>

            <p className="mt-6 text-slate-400 leading-relaxed">
              Some apps offered reminders or fragments of travel data. Nothing accounted for the real
              complexity of door-to-gate travel. That's when Tripsee was born: an assistant that thinks
              like a seasoned traveler, tracking the inbound aircraft, planning around boarding time,
              and remembering the details you'd forget.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-[#101315] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-5">What is Tripsee?</p>
            <h2 className="font-display text-2xl sm:text-4xl font-bold tracking-tight text-white leading-tight">
              A smart travel assistant that guides you from doorstep to gate, with confidence, not
              guesswork.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p} delay={i * 0.06}>
                <div className="glass-panel rounded-xl p-6 flex items-center gap-4 hover:border-primary/30 transition-colors duration-300">
                  <span className="font-mono text-xs text-primary">0{i + 1}</span>
                  <p className="text-sm font-medium text-slate-200">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 max-w-3xl">
            <h3 className="font-display text-xl sm:text-2xl font-bold text-white">Why now?</h3>
            <p className="mt-4 text-slate-400 leading-relaxed">
              Because travel shouldn't feel like a gamble. Tripsee is for the parent racing home to
              tuck their kids in, the student flying solo for the first time, and the road warrior on
              segment eighty of the year. We're not another itinerary manager. We're here to simplify
              the part of travel that stresses people out the most: getting to your flight on time.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1}>
              <div className="h-full glass-panel rounded-2xl p-8 hover:border-primary/30 transition-colors duration-300">
                <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/25 flex items-center justify-center text-primary">
                  <c.icon size={20} />
                </div>
                <h3 className="mt-6 font-display text-xl font-bold text-white">{c.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBanner title="Travel like the seasoned flyer you were meant to be." />
    </main>
  );
}
